import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const TILE_COLORS = {
  2: "#abc9eb",
  4: "#89aed8",
  8: "#6999cf",
  16: "#4c83c2",
  32: "#4ca6c2",
  64: "#59b8a7",
  128: "#82b879",
  256: "#b6bf73",
  512: "#baa5d9",
  1024: "#e0a580",
  2048: "#ff9f43",
};

const NextBlockPreview = ({
  nextBlock,
  upcomingBlock,
  hideLabel,
  isSkipAnimation = false,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPoofing, setIsPoofing] = useState(false);

  // Trigger animation when nextBlock changes
  useEffect(() => {
    if (isSkipAnimation) {
      // Poof animation for skip
      setIsPoofing(true);
      const poofTimer = setTimeout(() => setIsPoofing(false), 400);
      return () => clearTimeout(poofTimer);
    } else {
      // Regular bounce animation
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [nextBlock, isSkipAnimation]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        minWidth: 0,
      }}
    >
      {!hideLabel && (
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "#222", mr: 1, minWidth: 36 }}
        >
          Next:
        </Typography>
      )}
      {/* Next block (emphasized) */}
      <Box
        sx={{
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: TILE_COLORS[nextBlock] || "#eee",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          border: "2px solid #7fd6f7",
          mx: 0.5,
          transition: "all 0.2s",
          transform: isAnimating
            ? "scale(1.1)"
            : isPoofing
            ? "scale(0.8)"
            : "scale(1)",
          opacity: isPoofing ? 0.3 : 1,
          animation: isAnimating
            ? "bounce 0.3s ease-in-out"
            : isPoofing
            ? "poof 0.4s ease-in-out"
            : "none",
          "@keyframes bounce": {
            "0%, 100%": { transform: "scale(1)" },
            "50%": { transform: "scale(1.15)" },
          },
          "@keyframes poof": {
            "0%": {
              transform: "scale(1) rotate(0deg)",
              opacity: 1,
              filter: "blur(0px)",
            },
            "50%": {
              transform: "scale(1.2) rotate(180deg)",
              opacity: 0.5,
              filter: "blur(2px)",
            },
            "100%": {
              transform: "scale(0.8) rotate(360deg)",
              opacity: 0.3,
              filter: "blur(4px)",
            },
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#222" }}>
          {nextBlock}
        </Typography>
      </Box>
      {/* Upcoming block (less emphasized) */}
      <Box
        sx={{
          width: 26,
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: TILE_COLORS[upcomingBlock] || "#eee",
          borderRadius: 2,
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          border: "2px solid #b3e3f7",
          opacity: 0.6,
          mx: 0.5,
          transition: "all 0.2s",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#222" }}>
          {upcomingBlock}
        </Typography>
      </Box>
    </Box>
  );
};

export default NextBlockPreview;
